// ── 급여 설정 탭 ─────────────────────────────────────────────────────────────
// 1. 회사별 급여 기간/지급일/휴일 지급일 처리/근태 차감 규칙
// 2. 지급/공제 항목 CRUD  → PayItemsSection.jsx
// 3. 4대보험 요율 관리    → InsuranceRatesSection.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import PayItemsSection from './PayItemsSection';
import InsuranceRatesSection from './InsuranceRatesSection';
import HolidaySection from './HolidaySection';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 근태 차감 항목 정의 ──────────────────────────────────────────────────────
const DEDUCTION_KEYS = [
  { key: 'absent',          label: '결근',       methods: ['daily_rate', 'fixed'] },
  { key: 'late',            label: '지각',       methods: ['fixed', 'hourly_rate'] },
  { key: 'early_leave',     label: '조퇴',       methods: ['fixed', 'hourly_rate'] },
  { key: 'unpaid_leave',    label: '무급휴가',   methods: ['daily_rate', 'fixed'] },
  { key: 'excess_annual',   label: '연차초과',   methods: ['daily_rate', 'fixed'] },
];
const METHOD_LABELS = { daily_rate: '일급 공제', hourly_rate: '시급 공제', fixed: '고정 금액' };

// ── 휴일 지급일 처리 옵션 ────────────────────────────────────────────────────
const HOLIDAY_PAY_RULES = [
  { value: 'prev_business_day', label: '직전 영업일로 당김' },
  { value: 'next_business_day', label: '다음 영업일로 연기' },
  { value: 'as_is',             label: '변경 없음 (해당일 그대로)' },
];

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
const sectionTitle = { fontSize: 14, fontWeight: 800, color: 'var(--text-sub)', marginBottom: 12 };
const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnPrimary = { ...stdBlackBtn };

export default function PayrollSettings({ companyCode }) {
  const { showToast } = useToast();

  // ── 상태 ───────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 설정 폼 (급여 기간 + 지급일 + 휴일 처리 + 근태 차감)
  const [form, setForm] = useState({
    pay_period_start_day: 1,
    pay_day: 25,
    holiday_pay_rule: 'prev_business_day',
    deduction_rules: {},
  });

  // ── 데이터 로드 ────────────────────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.get(`/api/admin/payroll/settings?company_code=${companyCode}`);
    if (res.ok && res.data?.data) {
      const d = res.data.data;
      setForm({
        pay_period_start_day: d.pay_period_start_day ?? 1,
        pay_day: d.pay_day ?? 25,
        holiday_pay_rule: d.holiday_pay_rule || 'prev_business_day',
        deduction_rules: d.deduction_rules || {},
      });
    }
    setLoading(false);
  }, [companyCode]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // ── 회사 설정 저장 ─────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setSaving(true);
    const res = await apiClient.put('/api/admin/payroll/settings', {
      company_code: companyCode, ...form,
    });
    setSaving(false);
    if (res.ok) showToast('급여 설정이 저장되었습니다.', 'success');
    else showToast(res.data?.message || '저장 실패', 'error');
  };

  // ── 근태 차감 규칙 변경 헬퍼 ───────────────────────────────────────────────
  // enabled 토글 시 기본 method도 함께 저장 (미지정 방지)
  const updateDeductionRule = (key, field, value) => {
    setForm((prev) => {
      const existing = prev.deduction_rules[key] || {};
      const updated = { ...existing, [field]: value };
      // enabled를 켤 때 method가 없으면 해당 항목의 첫 번째 method를 기본값으로 설정
      if (field === 'enabled' && value === true && !updated.method) {
        const dk = DEDUCTION_KEYS.find((d) => d.key === key);
        if (dk) updated.method = dk.methods[0];
      }
      return { ...prev, deduction_rules: { ...prev.deduction_rules, [key]: updated } };
    });
  };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── 1. 급여 기간 / 지급일 / 휴일 처리 설정 ──────────────────────────── */}
      <div style={cardStyle}>
        <div style={sectionTitle}>급여 기간 / 지급일</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>급여 산정 시작일 (매월)</label>
            <select style={inputStyle} value={form.pay_period_start_day} onChange={(e) => setForm({ ...form, pay_period_start_day: Number(e.target.value) })}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{d}일</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>급여 지급일 (매월)</label>
            <select style={inputStyle} value={form.pay_day} onChange={(e) => setForm({ ...form, pay_day: Number(e.target.value) })}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>{d}일</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>지급일이 휴일인 경우</label>
            <select style={inputStyle} value={form.holiday_pay_rule} onChange={(e) => setForm({ ...form, holiday_pay_rule: e.target.value })}>
              {HOLIDAY_PAY_RULES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 근태 차감 규칙 */}
        <div style={{ marginTop: 16 }}>
          <div style={{ ...sectionTitle, marginBottom: 8 }}>근태 차감 규칙</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
            각 항목별로 차감 적용 여부와 방식을 설정하세요. 비활성화하면 해당 항목은 급여에서 차감되지 않습니다.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {DEDUCTION_KEYS.map(({ key, label, methods }) => {
              const rule = form.deduction_rules[key] || {};
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)',
                }}>
                  {/* on/off 토글 */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', minWidth: 70, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={rule.enabled ?? false}
                      onChange={(e) => updateDeductionRule(key, 'enabled', e.target.checked)}
                    />
                    {label}
                  </label>
                  {/* 차감 방식 */}
                  <select
                    style={{ ...inputStyle, width: 120, opacity: rule.enabled ? 1 : 0.4 }}
                    value={rule.method || methods[0]}
                    disabled={!rule.enabled}
                    onChange={(e) => updateDeductionRule(key, 'method', e.target.value)}
                  >
                    {methods.map((m) => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
                  </select>
                  {/* 고정 금액일 때만 금액 입력 */}
                  {(rule.method === 'fixed') && (
                    <input
                      style={{ ...inputStyle, width: 100, opacity: rule.enabled ? 1 : 0.4 }}
                      value={rule.amount != null ? fmt(rule.amount) : ''}
                      disabled={!rule.enabled}
                      placeholder="금액"
                      onChange={(e) => updateDeductionRule(key, 'amount', parse(e.target.value))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnPrimary} onClick={saveSettings} disabled={saving}>
            {saving ? '저장 중...' : '설정 저장'}
          </button>
        </div>
      </div>

      {/* ── 2. 휴일 관리 (시스템 공휴일 + 회사 휴일) ─────────────────────── */}
      <HolidaySection companyCode={companyCode} showToast={showToast} />

      {/* ── 3. 지급/공제 항목 관리 ──────────────────────────────────────────── */}
      <PayItemsSection companyCode={companyCode} showToast={showToast} />

      {/* ── 4. 4대보험 요율 ─────────────────────────────────────────────────── */}
      <InsuranceRatesSection showToast={showToast} />
    </div>
  );
}
